import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { PhoneInputWithFlags } from "../../common/PhoneInputWithFlags";
import { colors } from "../../ui/colors";
import { inputStyles } from "../../ui/styles/inputStyles";
import { PasswordStrengthChecker } from "../utils/PasswordStrengthChecker";

export const AdminAccountInformationForm = ({ control, errors, setValue, invitedEmail }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordValue = useWatch({ control, name: "password" });

  const emailDisplay = useMemo(() => invitedEmail || "", [invitedEmail]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          color: colors.gray.base,
          fontWeight: 600,
          fontSize: "20px",
        }}
      >
        Complete your profile
      </Typography>

      <Box sx={{ display: "flex", gap: 5, mb: 4, flexDirection: { xs: "column", md: "row" } }}>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: "First name is required" }}
          render={({ field }) => (
            <TextField {...field} fullWidth label="Name" error={!!errors.firstName} helperText={errors.firstName?.message} sx={inputStyles} />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{ required: "Last name is required" }}
          render={({ field }) => (
            <TextField {...field} fullWidth label="Last Name" error={!!errors.lastName} helperText={errors.lastName?.message} sx={inputStyles} />
          )}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 5, mb: 3 }}>
        <TextField
          value={emailDisplay}
          fullWidth
          label="Email Address"
          disabled
          sx={{
            ...inputStyles,
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: colors.gray.base,
              opacity: 0.9,
            },
          }}
          helperText="Email is set by the invitation."
        />
      </Box>

      <Box sx={{ display: "flex", gap: 5, mb: 3 }}>
        <PhoneInputWithFlags name={"phone"} countryCodeName="countryCode" setValue={setValue} control={control} errors={errors} required={false} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            minLength: { value: 8, message: "Password must be at least 8 characters" },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword((s) => !s)} sx={{ color: colors.gray[400] }}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              sx={inputStyles}
            />
          )}
        />

        <Box sx={{ ml: 1, mb: 1 }}>
          <PasswordStrengthChecker password={passwordValue || ""} />
        </Box>

        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: "Please confirm your password",
            validate: (value, formValues) => value === formValues.password || "Passwords do not match",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowConfirmPassword((s) => !s)} sx={{ color: colors.gray[400] }}>
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              sx={inputStyles}
            />
          )}
        />
      </Box>
    </Box>
  );
};
